import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { IconButton } from '@mui/material'
import { ChevronLeftSharp, ChevronRightSharp, KeyboardArrowDown } from '@mui/icons-material'
import {
  DarkBlurBackground,
  HoleGrid,
  HoleOption,
  HolePickerButton,
  HolePickerDropdown,
  HoleSelectorContainer,
  HoleSelectors,
  HoleSelectorsOverlay,
} from 'components/gps-editor/CourseGpsEditorElements.styles'

interface Props {
  activeHole: number
  atLeastOneHoleAdded: boolean
  goToHole: (hole: number) => void
  goToNextHole: () => void
  goToPreviousHole: () => void
  holesWithMarkers: Set<number>
}

export default function CourseGpsHoleNavigator({
  activeHole,
  atLeastOneHoleAdded,
  goToHole,
  goToNextHole,
  goToPreviousHole,
  holesWithMarkers,
}: Props): JSX.Element {
  const intl = useIntl()
  const [holePickerOpen, setHolePickerOpen] = useState<boolean>(false)

  const holes = useMemo(() => {
    return Array.from({ length: 18 }, (_, index) => index + 1)
  }, [])

  const handleHolePickerClick = () => {
    setHolePickerOpen((prev) => !prev)
  }

  const handleHolePickerClose = () => {
    setHolePickerOpen(false)
  }

  const handleHoleSelect = (hole: number) => {
    goToHole(hole)
    handleHolePickerClose()
  }

  return (
    <HoleSelectorsOverlay>
      <ClickAwayListener onClickAway={handleHolePickerClose}>
        <HoleSelectorContainer>
          <HoleSelectors>
            <IconButton color="primary" onClick={goToPreviousHole} disabled={!atLeastOneHoleAdded}>
              <ChevronLeftSharp color="primary" />
            </IconButton>
            <HolePickerButton type="button" onClick={handleHolePickerClick}>
              {intl.formatMessage({ id: 'strings.hole' })} {activeHole}
              <KeyboardArrowDown fontSize="small" htmlColor="#fff" />
            </HolePickerButton>
            <IconButton color="primary" onClick={goToNextHole} disabled={!atLeastOneHoleAdded}>
              <ChevronRightSharp color="primary" />
            </IconButton>
          </HoleSelectors>

          {holePickerOpen && (
            <HolePickerDropdown>
              <DarkBlurBackground>
                <HoleGrid>
                  {holes.map((hole) => {
                    const selected = activeHole === hole
                    const hasMarkers = holesWithMarkers.has(hole)

                    return (
                      <HoleOption
                        key={hole}
                        type="button"
                        $selected={selected}
                        $hasMarkers={hasMarkers}
                        onClick={() => handleHoleSelect(hole)}
                      >
                        {hole}
                      </HoleOption>
                    )
                  })}
                </HoleGrid>
              </DarkBlurBackground>
            </HolePickerDropdown>
          )}
        </HoleSelectorContainer>
      </ClickAwayListener>
    </HoleSelectorsOverlay>
  )
}
